// angular
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import {
  Component,
  forwardRef,
  inject,
  Injector,
  input,
  OnInit,
  output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
} from '@angular/forms';
import { CommasSeparateNumbersDirective } from '@app/shared/directives/commas-separate-numbers.directive';
import { OnlyNumberDirective } from '@app/shared/directives/only-number.directive';

type VARIANT = 'outlined' | 'default';
export type COLOR = 'success' | 'error' | 'white' | 'default';
type ICONPOSITION = 'right' | 'left';

@Component({
  selector: 'pwj-input',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    CommasSeparateNumbersDirective,
    OnlyNumberDirective,
  ],
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: InputComponent,
      multi: true,
    },
    {
      provide: NgControl,
      useExisting: forwardRef(() => InputComponent),
    },
  ],
})
export class InputComponent implements OnInit, ControlValueAccessor {
  public value: string | number | null = null;
  public disabled = false;
  public showPassword: boolean = false;

  // inputs
  label = input<string>('');
  subLabel = input<string>('');
  CustomCssSubLabel = input<string>('');
  placeholder = input('');
  name = input();
  type = input<string>('text');
  isDisabled = input<boolean>(false);
  isReadOnly = input<boolean>(false);
  color = input<COLOR>('default');
  variant = input<VARIANT>('outlined');
  icon = input<string>('');
  iconPosition = input<ICONPOSITION>('left');
  iconClasses = input<string>();
  inputValue = input<string>();
  messages = input<{ [errorCode: string]: string }>({});
  customCss = input<string>('');

  isTextAreaMode = input<boolean>(false);
  rows = input<string>('5');
  cols = input<string>('30');

  // outputs
  focus = output<any>();
  blur = output<any>();

  private _control!: NgControl;

  // dependencies
  private readonly _injector = inject(Injector);
  constructor() {}

  get hasErrors(): boolean {
    return !!(
      this._control.errors &&
      (this._control.touched || this._control.dirty) &&
      this._control.invalid &&
      !!this.messages()
    );
  }

  get getErrors(): ValidationErrors | null {
    return this._control.errors;
  }

  propagateChange = (_: any) => {};
  onTouched: () => void = () => {};

  writeValue(value: string) {
    this.value = value;
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  checkType(): string {
    return this.showPassword ? 'text' : 'password';
  }

  ngOnInit(): void {
    this._control = this._injector.get(NgControl);
    this.isDisabled && this.setDisabledState(this.isDisabled());

    // Set default value if its exist
    if (this.inputValue()) {
      this.writeValue(this.inputValue() as string);
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onFocus() {
    this.focus.emit('');
  }

  onBlur() {
    this.blur.emit('');
    this.onTouched();
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  protected readonly Number = Number;
}
